import { rootApi } from './baseApi';
import type { User, CreateUserRequest, UpdateUserRequest, BiometricsCredential } from '../types/user';

const transformRolesToType = (roles?: string | number[]): 'admin' | 'viewer' => {
  if (!roles) return 'viewer';
  
  // Handle roles as string (e.g. "1,2")
  if (typeof roles === 'string') {
    return roles.split(',').includes('1') ? 'admin' : 'viewer';
  }
  
  // Handle roles as number array
  return roles.includes(1) ? 'admin' : 'viewer';
};

const transformResponse = (user: any): User => ({
  id: user.id,
  email: user.email,
  firstName: user.firstName,
  lastName: user.lastName,
  phoneNumber: user.phoneNumber,
  isDisabled: user.isDisabled,
  isLockedOut: user.isLockedOut,
  role: transformRolesToType(user.roles),
  created_at: user.created_at,
  updated_at: user.updated_at,
  roles: typeof user.roles === 'string' ? user.roles.split(',').map(Number) : user.roles,
  hasBiometrics: Boolean(user.hasBiometrics)
});

export const userApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserById: builder.query<User, number>({
      query: (userId) => ({
        url: `/users/${userId}`,
        method: 'GET',
      }),
      transformResponse: transformResponse,
    }),

    getUsers: builder.query<User[], void>({
      query: () => ({
        url: '/users',
        method: 'GET',
      }),
      transformResponse: (response: any[]) => 
        response.map(transformResponse),
      providesTags: ['Users'],
    }),

    createUser: builder.mutation<User, CreateUserRequest>({
      query: (userData) => ({
        url: '/users',
        method: 'POST', 
        body: {
          email: userData.email,
          password: userData.password,
          firstName: userData.firstName,
          lastName: userData.lastName,
          phoneNumber: userData.phoneNumber,
          isDisabled: userData.isDisabled,
          isLockedOut: userData.isLockedOut,
          roles: userData.roles || [2]
        },
      }),
      transformResponse: transformResponse,
      invalidatesTags: ['Users'],
    }),

    updateUser: builder.mutation<User, UpdateUserRequest>({
      query: ({ userId, ...update }) => ({
        url: `/users/${userId}`,
        method: 'PATCH',
        body: update,
      }),
      transformResponse: transformResponse,
      invalidatesTags: ['Users'],
    }),

    deleteUser: builder.mutation<void, number>({
      query: (userId) => ({
        url: `/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),

    checkBiometricsStatus: builder.query<{ hasBiometrics: boolean }, { userId: number }>({
      query: ({ userId }) => ({
        url: `/users/${userId}/biometrics`,
        method: 'GET',
      }),
      transformResponse: (response: any) => ({
        hasBiometrics: Boolean(response.hasBiometrics)
      }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useCheckBiometricsStatusQuery
} = userApi;